<template>
  <div 
    :class="{ 'pdf-viewer-container custom-scroll': true, 'expanded': isExpanded, 'thumbnail': isThumbnail }"
    :style="!mutliplePdf ? 'max-height:960px;' : $isMobile()?'height: 5rem; width: 6rem;':'height: 8rem; width: 6rem;' "
    >
    <div v-if="showHeader" :class="{ 'py-2': !isExpanded, 'w-100': isExpanded }" :style="`width: ${!isExpanded ? '100%' : ''}`">
      <attachment-button-header
        :attachmentUrl="url"
        :is-expanded="isExpanded"
        @close-attachment="closePDF"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @download-attachment="downloadPDF"
      />
    </div>
    <div 
      style="overflow: auto;" 
      id="wrapper" 
      :style="{
       'width': !isExpanded ? `100%` : `${45 + (zoom - 10) * 10}vw`,
       'left': `100%`
      }"
      tabindex="0" 
      @keydown.esc="closePDF"
    >
      <div :id="isThumbnail ? 'thumbnailPageContainer' : 'pageContainer'" ref="container" @click.stop="isThumbnail ? $emit('go-to') : !isExpanded && expand()"
        :class="[{pointer: !isExpanded}]">
        <div  style="display: flex; align-items: center; justify-content: center; flex-direction: column;">
          <!-- <base-spinner v-if="wait" class="mx-auto" style="margin-top: 100px"></base-spinner> -->
          <canvas :id="`${randomCanvasId}-${idx}`" v-show="!wait" v-for="idx in totalPages" :key="idx" :class="[{pageBreakLine: !isThumbnail}]"
          ></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import AttachmentButtonHeader from './attachmentButtonHeader.vue';


import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';



const MAX_PAGES_RENDERED = 20; // Limit pages rendered to prevent overloading browser;

export default {
  name: "pdf-viewer",
  props: {
    "showHeader":{type:Boolean, default:true},
    "url":{type:String},
    "blob":{type:Object, default:null},
    'forceExpansion':{type:Boolean, default:false},
    'name':{type:String},
    'isThumbnail':{type:Boolean, default:false},
    'left':{type:Number, default:0},
    'mutliplePdf':{type:Boolean, default:false},
    'enableOverlayEffect':{type:Boolean, default:false},
  },
  components:{
    AttachmentButtonHeader,
  },
  // inject: ['enableOverlay', 'disableOverlay'],
  data() {
    return {
      zoom: 10,
      totalPages: 0,
      clickedExpansion: false,

      pdfDoc: null,

      // Variables for throttling
      wait: null,
      recall: false, // In case re-rendering is triggered
      isRendering: false,

      link: null,
    };
  },
  mounted() {
    this.setZoomBasedOnWidth();
    this.getPdf(this.url);
    window.addEventListener('resize', this.zoomAndRender);
    // if (this.enableOverlayEffect) this.enableOverlay();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.zoomAndRender);
  },
  methods: {
    async expand() {
      this.clickedExpansion = true;
      // this.enableOverlay();
      await this.throttledRenderPages();
    },
    async closePDF() {
      this.clickedExpansion = false;
      this.zoom = 10;
      // this.disableOverlay();
      await this.throttledRenderPages();
      this.$emit("closePDF");
    },
    downloadPDF() {
      try {
        if (this.blob) {
          // Use the provided blob directly
          const filename = this.name && this.name.indexOf('.pdf') != -1 ? this.name : `${this.name || 'document'}.pdf`;
          saveAs(this.blob, filename);
        } else if (this.url) {
          // Fallback to fetching from URL
          fetch(this.url, {method: 'GET'})
          .then(res => {
            return res.blob();
          })
          .then(blob => {
            const filename = this.name && this.name.indexOf('.pdf') != -1 ? this.name : `${this.name || 'document'}.pdf`;
            saveAs(blob, filename);
          })
          .catch(err => {
            console.error('err: ', err);
            this.$showAlert(err?.message ?? "Something went wrong");
          });
        } else {
          this.$showAlert("No PDF data available for download");
        }
      } catch (err) {
        console.error('Download error: ', err);
        this.$showAlert(err?.message ?? "Something went wrong during download");
      }
    },
    zoomIn() {
      if (this.zoom<15) {
        this.zoom+=1;
        this.throttledRenderPages();
      }
    },
    zoomOut() {
      if(this.zoom >10) {
        this.zoom-=1;
        this.throttledRenderPages();
      }
    },
    setZoomBasedOnWidth() {
      const width = window.innerWidth;
      this.zoom = 10;
    },
    zoomAndRender(){
      this.setZoomBasedOnWidth();
      this.throttledRenderPages();
    },
    // Function to wait for an element to be created before looking for it
    waitForElm(selector) {
      return new Promise(resolve => {
          if (document.querySelector(selector)) {
              return resolve(document.querySelector(selector));
          }

          const observer = new MutationObserver(() => {
              if (document.querySelector(selector)) {
                  resolve(document.querySelector(selector));
                  observer.disconnect();
              }
          });

          observer.observe(document.body, {
              childList: true,
              subtree: true
          });
      });
    },
    calcRatio(viewport, client) {
      if (client==0) {
        return 1;
      }
      return viewport/client;
    },
    async renderPage(pdfDoc, pageNumber, container) {
      let page = await pdfDoc.getPage(pageNumber);
      let canvas = await this.waitForElm(`#${this.randomCanvasId}-${pageNumber}`);
      let basicViewport = page.getViewport({scale: 1});
      let viewport = page.getViewport({scale: this.calcRatio(container.clientWidth, basicViewport.width)});
      let context = canvas.getContext('2d');

      let outputScale = window.devicePixelRatio || 1;

      if (this.isThumbnail) {
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        // canvas.style.width = Math.floor(viewport.width) + "px";
        canvas.style.height = "8rem"; // Keep same as .thumbnail
      }
      else if (!this.isExpanded){
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = Math.floor(viewport.width)*(this.zoom/10) + "px";
        canvas.style.height =  Math.floor(viewport.height)*(this.zoom/10) + "px";
      }
      else {
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = Math.floor(viewport.width) + "px";
        canvas.style.height =  Math.floor(viewport.height) + "px";
      }

      let transform = outputScale !== 1
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null;

      let renderContext = {
        canvasContext: context,
        viewport: viewport,
        transform: transform,
      };
      return page.render(renderContext).promise;
    },
    // Renders min of 20 or total pages of an Invoices
    async renderAllPages(pdfDoc=this.pdfDoc) {
      if (this.isRendering) {
        return;
      }
      this.isRendering = true;
      const renderCalls = [];
      const container = await this.waitForElm('#pageContainer');
      for (let i=1;i<=Math.min(MAX_PAGES_RENDERED, this.totalPages); i++) {
          renderCalls.push(this.renderPage(pdfDoc, i, container));
        }
      return new Promise((resolve) => {
        Promise.all(renderCalls).finally(() => {
          this.isRendering = false;
          resolve();
        });
      });
    },

    // Throttle page rendering so we don't retry re-rending 100 times while re-sizing a browser window
    throttledRenderPages() {
      const DELAY = 400;

      function checkRecall () {
        if (this.recall == false) {
          this.wait = false;
        } else if (this.isRendering) {
          this.recall = true;
          setTimeout(checkRecall.bind(this), DELAY);
        } else {
          this.renderAllPages();
          this.recall = false;
          setTimeout(checkRecall.bind(this), DELAY);
        }
      }

        if (this.wait || this.isRendering) {
          this.recall = true;
          return;
        }
        this.renderAllPages();
        this.wait = true;
        setTimeout(checkRecall.bind(this), DELAY);
    },
    // Simply fetches the pdf on load
    async getPdf(url) {
      if (url) {
        const loadingTask = pdfjsLib.getDocument(url);
        try {
          this.pdfDoc = await loadingTask.promise;
          this.totalPages = this.isThumbnail ? this.pdfDoc.numPages:this.pdfDoc.numPages;
          await this.throttledRenderPages();
          this.$emit("loading-success", true);
        }
        catch(err) {
          if (err.name === "MissingPDFException") {
            this.$showAlert("PDF does not exist");
          } else {
            this.$showAlert("Something went wrong fetching pdf");
          }
          this.$emit("loading-failed", true);
        }
      }
    },
  },
  computed: {
    isExpanded() {
      if (this.clickedExpansion || this.forceExpansion)
        return true;
      else
        return false;
    },
    randomCanvasId() {
      return `canvas-${Math.floor(Math.random() * 800)}`;
    }
  },
  watch: {
    url(val) {
      if (val) {
        this.getPdf(val);
      }
    },
  }
};
</script>

<style scoped lang="scss">
.custom-scroll::-webkit-scrollbar {
  display: none; 
}
@import './pdfViewer.scss'
</style>

